/**
 * ReconciliationNewPage.js
 * State-of-the-art reconciliation interface with modern, elegant design
 */

import React, { useState, useCallback } from 'react';
import { Box } from '@mui/material';

import { useReconciliationNew } from './hooks/useReconciliationNew';
import ReconciliationHeader from './ReconciliationHeader';
import SelectionSummary from './SelectionSummary';
import UploadOverviewTab from './UploadOverviewTab';
import ReconciliationTab from './ReconciliationTab';
import CreateEntryFromTransactionDialog from './CreateEntryFromTransactionDialog';
import { toastNotification } from '../../shared/toastNotification';

// Import entry editing components
import EntryEditFormComponent from '../entries/EntryEditFormComponent';
import DeleteEntryConfirmDialog from './DeleteEntryConfirmDialog';

const ReconciliationNewPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [createEntryDialog, setCreateEntryDialog] = useState({
    open: false,
    transaction: null,
    transactions: null, // Para modo bulk
    bulkEditPairs: null,
  });
  const [editEntryDialog, setEditEntryDialog] = useState({
    open: false,
    entry: null,
  });
  const [deleteEntryDialog, setDeleteEntryDialog] = useState({
    open: false,
    entry: null,
  });

  const {
    // Data
    statements,
    transactions,
    journalEntries,
    stats,

    // State
    selectedStatement,
    selectedTransactions,
    selectedJournalEntries,
    isUploading,
    uploadProgress,
    transactionTypeFilter,
    reconciliationFilter,

    // Loading states
    isLoadingStatements,
    isLoadingTransactions,
    isLoadingJournalEntries,
    isLoadingStats,

    // Calculated values
    selectedTransactionTotal,
    selectedJournalEntryTotal,
    selectionDifference,
    canReconcile,

    // Actions
    setSelectedStatement,
    handleFileUpload,
    handleReconciliation,
    executeUnreconciliation,
    handleDeleteStatement,
    handleBulkUnreconcileStatement,
    findAutomaticMatches,
    toggleTransactionSelection,
    toggleJournalEntrySelection,
    clearSelections,
    refreshAllData,
    setTransactionTypeFilter,
    setReconciliationFilter,
  } = useReconciliationNew();

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleStatementSelect = useCallback(
    (statement) => {
      setSelectedStatement(statement);
      clearSelections();
      setReconciliationFilter('all');
      // Auto-switch to reconciliation tab when statement is selected
      if (statement && activeTab === 0) {
        setActiveTab(1);
      }
    },
    [setSelectedStatement, clearSelections, setReconciliationFilter, activeTab]
  );

  const handleCreateEntry = useCallback((transaction) => {
    setCreateEntryDialog({
      open: true,
      transaction,
      transactions: null,
      bulkEditPairs: null,
    });
  }, []);
  const handleBulkEditStart = useCallback((pairs) => {
    if (!pairs?.length) return;

    setCreateEntryDialog({
      open: true,
      transaction: null,
      transactions: pairs.map((pair) => pair.transaction),
      bulkEditPairs: pairs,
    });
  }, []);

  const handleBulkCreateEntries = useCallback((transactions) => {
    if (!transactions?.length) return;

    setCreateEntryDialog({
      open: true,
      transaction: null,
      transactions,
      bulkEditPairs: null,
    });
  }, []);

  const handleCloseCreateEntryDialog = useCallback(() => {
    setCreateEntryDialog({
      open: false,
      transaction: null,
      transactions: null,
      bulkEditPairs: null,
    });
  }, []);

  const handleEdit = useCallback((entry) => {
    setEditEntryDialog({
      open: true,
      entry,
    });
  }, []);

  const handleDelete = useCallback((entry) => {
    setDeleteEntryDialog({
      open: true,
      entry,
    });
  }, []);

  const handleCloseEditEntryDialog = useCallback(() => {
    setEditEntryDialog({
      open: false,
      entry: null,
    });
  }, []);

  const handleCloseDeleteEntryDialog = useCallback(() => {
    setDeleteEntryDialog({
      open: false,
      entry: null,
    });
  }, []);

  const handleReconciliationSubmit = useCallback(
    async (pairsOverride = null, options = {}) => {
      if (!pairsOverride && !canReconcile) return;

    try {
      // Create N:N reconciliation - all selected transactions are reconciled with all selected journal entries as a group
      // This creates a many-to-many relationship where the group total matches
      const reconciliationPairs = [];

      // Generate a unique reconciliation group ID for this N:N matching
      const reconciliationGroupId = `group_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      // Each transaction in the selection gets reconciled with a reference to the journal entry group
      selectedTransactions.forEach((transaction, transactionIndex) => {
        selectedJournalEntries.forEach((journalEntry, journalIndex) => {
          reconciliationPairs.push({
            transaction,
            journalEntry,
            reconciliationGroupId,
            notes: `N:N ${selectedTransactions.length}x${selectedJournalEntries.length}`,
            isGroupReconciliation: true,
            groupInfo: {
              totalTransactions: selectedTransactions.length,
              totalJournalEntries: selectedJournalEntries.length,
              transactionTotal: selectedTransactionTotal,
              journalEntryTotal: selectedJournalEntryTotal,
            },
          });
        });
      });

      const pairsToSend = pairsOverride && pairsOverride.length ? pairsOverride : reconciliationPairs;
      await handleReconciliation(pairsToSend, options);
    } catch (error) {
      console.error('Reconciliation error:', error);

      // Handle specific error types with user-friendly messages
      let errorMessage = 'Erro na conciliação';
      let errorTitle = 'Erro';

      if (error.response?.data?.message) {
        const backendMessage = error.response.data.message;

        if (backendMessage.includes('already reconciled')) {
          errorTitle = 'Itens Já Conciliados';
          errorMessage =
            'Um ou mais itens selecionados já foram conciliados anteriormente. Atualize a página para ver os dados mais recentes.';
        } else if (
          backendMessage.includes('Transfer entries in reconciliation must have identical values')
        ) {
          errorTitle = 'Diferença de Valores';
          errorMessage =
            'Os valores diferem significativamente e as datas não são próximas o suficiente. Verifique se os valores estão corretos ou se as datas coincidem.';
          errorTitle = 'Mistura Inválida de Tipos';
          errorMessage =
            'Não é possível conciliar transferências com transações normais no mesmo grupo.';
        } else if (backendMessage.includes('Transfer is partially reconciled')) {
          errorTitle = 'Transferência Parcialmente Conciliada';
          errorMessage =
            'Esta transferência já foi parcialmente conciliada. Complete a conciliação nos dois bancos antes de prosseguir.';
        } else {
          errorMessage = backendMessage;
        }
      }

      toastNotification('error', errorMessage, errorTitle);
    }
    },
    [
      canReconcile,
      selectedTransactions,
      selectedJournalEntries,
      selectedTransactionTotal,
      selectedJournalEntryTotal,
      handleReconciliation,
    ]
  );

  const handleBulkEditConfirm = useCallback(
    async (pairs) => {
      if (!pairs?.length) return;

      const pairsToSend = pairs.map((pair) => {
        const pairId =
          pair.id || `pair_${pair.transaction?.transactionHash || pair.journalEntry?.id}`;

        return {
          id: pairId,
          transaction: pair.transaction,
          journalEntry: pair.journalEntry,
          reconciliationGroupId: pairId,
          notes: 'Conciliacao 1:1 por valor (multipla)',
        };
      });

      await handleReconciliationSubmit(pairsToSend, { allowMultipleTransactions: true });
    },
    [handleReconciliationSubmit]
  );

  const getTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <UploadOverviewTab
            statements={statements}
            stats={stats}
            selectedStatement={selectedStatement}
            isLoadingStatements={isLoadingStatements}
            isLoadingStats={isLoadingStats}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            onFileUpload={handleFileUpload}
            onStatementSelect={handleStatementSelect}
            onStatementDelete={handleDeleteStatement}
            onStatementBulkUnreconcile={handleBulkUnreconcileStatement}
          />
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Selection Summary - Only in Reconciliation Tab */}
            <SelectionSummary
              selectedStatement={selectedStatement}
              selectedTransactions={selectedTransactions}
              selectedJournalEntries={selectedJournalEntries}
              canReconcile={canReconcile}
              selectionDifference={selectionDifference}
              selectedTransactionTotal={selectedTransactionTotal}
              selectedJournalEntryTotal={selectedJournalEntryTotal}
              onReconcile={handleReconciliationSubmit}
              onClearSelections={clearSelections}
              onBulkCreateEntries={handleBulkCreateEntries}
            />

            <ReconciliationTab
              selectedStatement={selectedStatement}
              transactions={transactions}
              journalEntries={journalEntries}
              selectedTransactions={selectedTransactions}
              selectedJournalEntries={selectedJournalEntries}
              isLoadingTransactions={isLoadingTransactions}
              isLoadingJournalEntries={isLoadingJournalEntries}
              transactionTypeFilter={transactionTypeFilter}
              reconciliationFilter={reconciliationFilter}
              selectedTransactionTotal={selectedTransactionTotal}
              selectedJournalEntryTotal={selectedJournalEntryTotal}
              selectionDifference={selectionDifference}
              canReconcile={canReconcile}
              onTransactionToggle={toggleTransactionSelection}
              onJournalEntryToggle={toggleJournalEntrySelection}
              onReconcile={handleReconciliationSubmit}
              onFindMatches={findAutomaticMatches}
              onTransactionTypeFilterChange={setTransactionTypeFilter}
              onReconciliationFilterChange={setReconciliationFilter}
              executeUnreconciliation={executeUnreconciliation}
              onCreateEntry={handleCreateEntry}
              onBulkEditStart={handleBulkEditStart}
              onBulkCreateEntries={handleBulkCreateEntries}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: 'grey.50',
        pb: { xs: 2, md: 3 },
      }}
    >
      <ReconciliationHeader
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        stats={stats}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        statements={statements}
        selectedStatement={selectedStatement}
      />

      {/* Professional Layout with Flexible Grid */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 2, md: 3 },
          gap: 2,
          minHeight: 0,
        }}
      >
        {/* Main Content Area - Flexible Layout */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            width: '100%',
          }}
        >
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            {getTabContent()}
          </Box>
        </Box>
      </Box>

      <CreateEntryFromTransactionDialog
        open={createEntryDialog.open}
        onClose={handleCloseCreateEntryDialog}
        transaction={createEntryDialog.transaction}
        transactions={createEntryDialog.transactions}
        bulkEditPairs={createEntryDialog.bulkEditPairs}
        onBulkEditConfirm={handleBulkEditConfirm}
        selectedStatement={selectedStatement}
        refreshAllData={refreshAllData}
        clearSelections={clearSelections}
      />

      {/* Edit Entry Dialog */}
      {editEntryDialog.entry && (
        <EntryEditFormComponent
          isShown={editEntryDialog.open}
          setIsShown={handleCloseEditEntryDialog}
          journalEntry={editEntryDialog.entry}
          onSuccess={() => {
            clearSelections();
            refreshAllData();
          }}
        />
      )}

      {/* Delete Entry Dialog */}
      <DeleteEntryConfirmDialog
        open={deleteEntryDialog.open}
        onClose={handleCloseDeleteEntryDialog}
        onConfirm={async () => {
          if (!deleteEntryDialog.entry) return;

          try {
            const { deleteEntry } = await import('../../services/api/entriesService');
            await deleteEntry(deleteEntryDialog.entry.id);

            toastNotification('success', 'Lançamento excluído com sucesso!');
            handleCloseDeleteEntryDialog();
            clearSelections(); // Clear selections after successful deletion
            refreshAllData(); // Refresh the data after deletion
          } catch (error) {
            console.error('Error deleting entry:', error);
            toastNotification('error', 'Erro ao excluir o lançamento. Tente novamente.');
          }
        }}
        entry={deleteEntryDialog.entry}
      />
    </Box>
  );
};

export default ReconciliationNewPage;
